import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Table, Input, Message, Tab, Pagination } from "semantic-ui-react";
import { DateInput } from "semantic-ui-calendar-react";
import moment from "moment";
import toastr from "toastr";
import { getPKOReportApi } from "../../../../../../../apis/reports/pkoReportsApi";

// This component contains the shared report table UI
const ReportTableContent = ({ visaType, initialPage, onPageChange }) => {
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [currentPage, setCurrentPage] = useState(initialPage || 1);
  const [sortDirection, setSortDirection] = useState("descending");
  const itemsPerPage = 20;

  // API data state
  const [reportData, setReportData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [totalPagesFromApi, setTotalPagesFromApi] = useState(1);

  const today = moment().format("DD-MMM-YYYY");
  const yesterday = moment().subtract(1, "days").format("DD-MMM-YYYY");

  const [startDate, setStartDate] = useState(yesterday);
  const [endDate, setEndDate] = useState(today);

  const handleStartDateChange = (event, { value }) => {
    if (!value) {
      setStartDate("");
      return;
    }

    const newStartMoment = moment(value, "DD-MMM-YYYY");
    if (!newStartMoment.isValid()) {
      toastr.error("Invalid 'From date' format.", "Date Error");
      return;
    }

    const currentEndMoment = moment(endDate, "DD-MMM-YYYY");

    if (currentEndMoment.isValid()) {
      if (newStartMoment.isAfter(currentEndMoment)) {
        toastr.error(
          "From date cannot be later than To date",
          "Date Range Error",
        );
      } else {
        const diffInDays = currentEndMoment.diff(newStartMoment, "days");
        if (diffInDays > 90) {
          toastr.error("Date range cannot exceed 90 days.", "Date Range Error");
        } else {
          setStartDate(value);
        }
      }
    } else {
      setStartDate(value);
    }
  };

  const handleEndDateChange = (event, { value }) => {
    if (!value) {
      setEndDate("");
      return;
    }

    const newEndMoment = moment(value, "DD-MMM-YYYY");
    if (!newEndMoment.isValid()) {
      toastr.error("Invalid 'To date' format.", "Date Error");
      return;
    }

    const currentStartMoment = moment(startDate, "DD-MMM-YYYY");

    if (currentStartMoment.isValid()) {
      if (newEndMoment.isBefore(currentStartMoment)) {
        toastr.error(
          "To date cannot be earlier than From date",
          "Date Range Error",
        );
      } else {
        const diffInDays = newEndMoment.diff(currentStartMoment, "days");
        if (diffInDays > 90) {
          toastr.error("Date range cannot exceed 90 days.", "Date Range Error");
        } else {
          setEndDate(value);
        }
      }
    } else {
      setEndDate(value);
    }
  };

  useEffect(() => {
    if (search.length >= 3 || search.length === 0) {
      setIsSearching(true);
      const timer = setTimeout(() => {
        setDebouncedSearch(search);
        setIsSearching(false);
      }, 500);
      return () => clearTimeout(timer);
    } else {
      if (search.length > 0 && search.length < 3) {
        setDebouncedSearch("");
      }
    }
  }, [search]);

  // Fetch data from API
  useEffect(() => {
    // If either date is missing, clear data and prevent API call
    if (!startDate || !endDate) {
      setReportData([]);
      setTotalPagesFromApi(1); // Reset pagination
      setIsLoading(false); // Ensure loading indicator is off
      setApiError(null); // Clear any previous API errors
      toastr.info(
        "Please select both a 'From' and 'To' date to view the report.",
        "Date Range Required",
      );
      return; // Skip the API call
    }

    const fetchData = async () => {
      setIsLoading(true);
      setApiError(null);
      try {
        const params = {
          search: debouncedSearch.length >= 3 ? debouncedSearch : "",
          startDate: startDate
            ? moment(startDate, "DD-MMM-YYYY").format("YYYY-MM-DD")
            : undefined,
          endDate: endDate
            ? moment(endDate, "DD-MMM-YYYY").format("YYYY-MM-DD")
            : undefined,
          page: currentPage,
          itemsPerPage,
          sortBy: "statusLogsCount",
          sortOrder: sortDirection === "ascending" ? "asc" : "desc",
        };
        const apiVisaType = visaType === "All" ? "all" : visaType;
        const response = await getPKOReportApi(apiVisaType, params);

        if (
          response &&
          response.data &&
          response.data.data &&
          response.data.pagination
        ) {
          setReportData(response.data.data);
          setTotalPagesFromApi(response.data.pagination.totalPages || 1);
        } else if (response && response.data && Array.isArray(response.data)) {
          setReportData(response.data);
          setTotalPagesFromApi(
            Math.ceil(response.data.length / itemsPerPage) || 1,
          );
        } else {
          setReportData([]);
          setTotalPagesFromApi(1);
        }
      } catch (error) {
        console.error("API call failed:", error);
        setApiError(error.message || "Failed to fetch report data.");
        setReportData([]);
        setTotalPagesFromApi(1);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [
    visaType,
    debouncedSearch,
    startDate,
    endDate,
    currentPage,
    itemsPerPage,
    sortDirection,
  ]);

  const currentItems = reportData;
  const totalPages = totalPagesFromApi;

  useEffect(() => {
    if (currentPage !== 1) {
      setCurrentPage(1);
      if (onPageChange) {
        onPageChange(1);
      }
    }
  }, [debouncedSearch, startDate, endDate, visaType]);

  useEffect(() => {
    if (initialPage !== undefined && initialPage !== currentPage) {
      setCurrentPage(initialPage);
    }
  }, [initialPage]);

  console.log({
    visaType,
    reportDataLength: reportData.length,
    currentItemsLength: currentItems.length,
    currentPage,
    totalPages,
    itemsPerPage,
    isLoading,
    apiError,
  });

  const handlePageChange = (e, { activePage }) => {
    setCurrentPage(activePage);
    if (onPageChange) {
      onPageChange(activePage);
    }
  };

  const handleSortChange = () => {
    setSortDirection(
      sortDirection === "ascending" ? "descending" : "ascending",
    );
  };

  return (
    <div>
      <div
        style={{
          backgroundColor: "#1a3a5f",
          padding: "15px 20px",
          display: "flex",
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <div
          style={{
            color: "white",
            fontSize: "18px",
            fontWeight: "bold",
            marginRight: "15px",
          }}
        >
          PKO Report - {visaType}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "30px" }}>
          <Input
            placeholder="Search by name (min 3 chars)"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ marginRight: "15px", width: "220px" }}
            loading={isSearching}
            icon="search"
          />

          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span style={{ color: "white", marginRight: "5px" }}>From:</span>
            <div style={{ marginRight: "15px" }}>
              <DateInput
                name="startDate"
                placeholder="From Date"
                value={startDate}
                iconPosition="left"
                dateFormat="DD-MMM-YYYY"
                animation="none"
                duration={0}
                onChange={handleStartDateChange}
                clearable
                style={{ width: "160px" }}
                popupPosition="top left"
                maxDate={today}
              />
            </div>
            <span style={{ color: "white", marginRight: "5px" }}>To:</span>
            <div>
              <DateInput
                name="endDate"
                placeholder="To Date"
                value={endDate}
                iconPosition="left"
                dateFormat="DD-MMM-YYYY"
                animation="none"
                duration={0}
                onChange={handleEndDateChange}
                clearable
                style={{ width: "160px" }}
                popupPosition="top left"
              />
            </div>
          </div>
        </div>
      </div>

      {isLoading && <Message info header="Loading report data..." />}
      {apiError && (
        <Message error header="Failed to load report" content={apiError} />
      )}

      {!isLoading && !apiError && reportData.length === 0 && (
        <Message
          warning
          header="No Data"
          content="No records found matching your criteria."
        />
      )}

      {!isLoading && !apiError && reportData.length > 0 && (
        <Table celled striped>
          <Table.Header>
            <Table.Row>
              <Table.HeaderCell>Updater</Table.HeaderCell>
              <Table.HeaderCell>Email</Table.HeaderCell>
              <Table.HeaderCell
                sorted={sortDirection}
                onClick={handleSortChange}
                style={{ cursor: "pointer" }}
              >
                Total Count
              </Table.HeaderCell>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {currentItems.map((row, idx) => (
              <Table.Row key={row.userId || idx}>
                <Table.Cell>
                  {`${row.firstName || ""} ${row.lastName || ""}`.trim() ||
                    "N/A"}
                </Table.Cell>
                <Table.Cell>{row.email || "N/A"}</Table.Cell>
                <Table.Cell>
                  {row.statusLogsCount === undefined
                    ? "N/A"
                    : row.statusLogsCount}
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table>
      )}

      {!isLoading && !apiError && (totalPages > 0 || reportData.length > 0) && (
        <div
          style={{
            display: "flex",
            justifyContent: "flex-end",
            margin: "20px 0",
          }}
        >
          <Pagination
            activePage={currentPage}
            onPageChange={handlePageChange}
            totalPages={Math.max(1, totalPages)}
            ellipsisItem={{ content: "...", icon: true }}
            firstItem={{ content: "«", icon: true }}
            lastItem={{ content: "»", icon: true }}
            prevItem={{ content: "‹", icon: true }}
            nextItem={{ content: "›", icon: true }}
          />
        </div>
      )}
    </div>
  );
};

const PKOReportTable = () => {
  const [ds11CurrentPage, setDS11CurrentPage] = useState(1);
  const [ds82CurrentPage, setDS82CurrentPage] = useState(1);
  const [combinedCurrentPage, setCombinedCurrentPage] = useState(1);
  const [activeIndex, setActiveIndex] = useState(0);

  console.log({
    ds11CurrentPage,
    ds82CurrentPage,
    combinedCurrentPage,
    activeIndex,
  });

  const handleDS11PageChange = (page) => {
    setDS11CurrentPage(page);
  };

  const handleDS82PageChange = (page) => {
    setDS82CurrentPage(page);
  };

  const handleCombinedPageChange = (page) => {
    setCombinedCurrentPage(page);
  };

  const handleTabChange = (e, { activeIndex }) => {
    setActiveIndex(activeIndex);
  };

  const panes = [
    {
      menuItem: "All",
      render: () => (
        <Tab.Pane>
          <ReportTableContent
            visaType="All"
            initialPage={combinedCurrentPage}
            onPageChange={handleCombinedPageChange}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "DS11",
      render: () => (
        <Tab.Pane>
          <ReportTableContent
            visaType="DS11"
            initialPage={ds11CurrentPage}
            onPageChange={handleDS11PageChange}
          />
        </Tab.Pane>
      ),
    },
    {
      menuItem: "DS82",
      render: () => (
        <Tab.Pane>
          <ReportTableContent
            visaType="DS82"
            initialPage={ds82CurrentPage}
            onPageChange={handleDS82PageChange}
          />
        </Tab.Pane>
      ),
    },
  ];

  return (
    <Tab
      panes={panes}
      activeIndex={activeIndex}
      onTabChange={handleTabChange}
    />
  );
};

export default PKOReportTable;
